/** Shared argument handling for the Data surface's commands. */

import { readFileSync } from 'node:fs';
import type { Command } from 'commander';
import { BASE_ID_RULE, PATH_SAFE_ID_RULE, isPathSafeId, isValidBaseId } from '../lib/base-id.js';
import { expected } from '../lib/expected.js';
import { promptSecret, readStdin, resolveSecretSource } from '../lib/utils.js';
import type { OutputFormat } from './output.js';

export interface DataGlobals {
  base?: string;
  org?: string;
  json?: boolean;
  output?: string;
}

/**
 * Validate and percent-encode an id on its way into a `/v1` request path.
 *
 * Interpolating an id raw is not merely untidy — `fetch()` parses the assembled
 * string with the URL parser, which resolves `..` segments, so an id of
 * `../../../setup/hubs/<uuid>` retargets the request at a DIFFERENT WayAI
 * endpoint while the command's own confirmation prompt still names a base. The
 * request stays inside the caller's authorization, but it acts on something
 * other than what the user was shown, and CLI ids routinely arrive from
 * agent-generated or pasted text.
 *
 * Validating rejects the malformed id with a message naming the argument;
 * encoding then makes the accepted grammar's own characters inert. Every id that
 * becomes a path segment goes through here — never a bare `encodeURIComponent`
 * at the call site, which is the check that gets forgotten.
 *
 * Path safety is ALL this enforces, and that is the whole reason it is separate
 * from `basePathSegment`. The ids it guards — a `record_type`, a `file_type`, a
 * toolset slug — carry no server-side charset rule (`isPathSafeId` says where
 * they come from), so anything narrower would refuse ids the API happily
 * returns. A BASE id does carry such a rule; it goes through `basePathSegment`.
 */
export function pathSegment(id: string, label = 'id'): string {
  if (!isPathSafeId(id)) {
    // `expected`, not a bare Error: a mistyped id is a user-actionable
    // condition, and an untagged throw reaches Sentry and the autofix triage
    // queue (`shouldReportToSentry` drops only tagged ones).
    throw expected(`Invalid ${label}: ${JSON.stringify(id)}. An id may use ${PATH_SAFE_ID_RULE}.`);
  }
  return encodeURIComponent(id);
}

/**
 * `pathSegment` for a base id the CALLER IS CHOOSING — the `PUT /v1/bases/:id`
 * upsert, and nothing else.
 *
 * The test is not "does the API validate here" but "can the person reading the
 * message act on it", and the two come apart in a way that decides every call
 * site:
 *
 *   - `PUT /v1/bases/:id` — FOUR commands, all the same upsert: `bases create`,
 *     `update`, `rename`, `tag`. (`update --timezone` reaches the id a second
 *     time for the settings read-modify-write; that is `update` again, not a
 *     fifth command.) The id came off the command line, so quoting the charset
 *     tells the caller exactly what to re-type, and doing it here costs no round
 *     trip. (The route validates the path id and, as its own comment says, cannot
 *     tell a create from an update.)
 *   - `POST /v1/:base_id/preview` DOES apply the rule server-side, and is still
 *     deliberately left on `pathSegment` — at `bases create-preview` AND at
 *     `config-as-code/api.ts`'s `createPreview`, which `wayai push` reaches with
 *     the `origin_base_id` a folder's `base.yaml` declares. The id at fault there
 *     is the ORIGIN's, which is immutable and not the caller's to re-spell, so
 *     `assertDerivablePreviewId` answers with the remedy instead — "create a base
 *     under a valid id and migrate to it" — and says in its own comment why it
 *     withholds the charset. Refusing locally would replace that with a rule the
 *     caller cannot act on.
 *   - Everything else — `GET`/`DELETE /v1/bases/:id`, every `/v1/:base_id/…`
 *     config and data-plane route, `promote` — the API deliberately still serves
 *     for a base whose stored id predates its rule. `validateBaseId`'s docblock
 *     in `apps/rekor/backend/src/durable-objects/_shared/validation.ts` enumerates
 *     what such a base keeps; refusing any of it here would lock the CLI out of a
 *     base the API answers for, including the `delete` that is half of the
 *     recovery path.
 *
 * `isValidBaseId` is the copy of the far-side rule; `BASE_ID_RULE` is the same
 * rule as a sentence, interpolated rather than re-spelled so the message cannot
 * drift from the check it reports.
 */
export function basePathSegment(id: string, label = 'base id'): string {
  if (!isValidBaseId(id)) {
    throw expected(`Invalid ${label}: ${JSON.stringify(id)}. Base ids are slugs — ${BASE_ID_RULE}.`);
  }
  return encodeURIComponent(id);
}

/**
 * One path segment from a value ANOTHER system minted — an `external_id`, or a
 * stored attachment key.
 *
 * `pathSegment`'s slug grammar is right for ids this platform issues, but an
 * upstream id legitimately carries characters it excludes (an email address, a
 * vendor key with `:` or `+`), and rejecting those would make the external-id
 * lookups unusable rather than safe. Encoding neutralizes all of them, `/`
 * included. The one thing encoding does NOT neutralize is a bare dot segment,
 * which `fetch()`'s URL parser still resolves — so that is the check kept here.
 */
export function foreignSegment(value: string, label = 'id'): string {
  if (value === '.' || value === '..') {
    throw expected(`Invalid ${label}: ${JSON.stringify(value)} is a path traversal segment.`);
  }
  return encodeURIComponent(value);
}

/**
 * A path whose `/` separators are SIGNIFICANT — a file's `<path>` and the
 * stored attachment key it maps to. `pathSegment` would reject the separator
 * and `foreignSegment` would encode it away, so each segment goes through
 * `foreignSegment` and the separators are rejoined.
 */
export function pathSegments(value: string, label = 'path'): string {
  return value
    .split('/')
    .map((segment) => foreignSegment(segment, label))
    .join('/');
}

/**
 * Parse an inline JSON argument, or `@path.json` to read one from disk.
 *
 * Tags its own failure `expected`. A malformed `--data '{'` or a missing
 * `@nope.json` is user input, and an untagged throw reaches Sentry and the
 * autofix triage queue (`shouldReportToSentry` drops only tagged ones) — so
 * every typo on any JSON-bearing flag filed a bug report against the platform.
 *
 * Tagged HERE rather than at each call site because this function has two dozen
 * of them, all with the same failure and the same right answer; a per-caller
 * `try`/`catch` is the layer that gets forgotten on the next one added. `flag`
 * is what the caller advertises, so the message can name the option the user
 * actually typed — the raw `Unexpected end of JSON input` names neither the flag
 * nor the file.
 */
export function parseData(data: string, flag?: string): Record<string, unknown> {
  const prefix = flag ? `${flag}: ` : '';
  const source = data.startsWith('@') ? data.slice(1) : undefined;
  let text = data;
  if (source !== undefined) {
    try {
      text = readFileSync(source, 'utf-8');
    } catch (e) {
      // Reading and parsing are surfaced separately: a missing file and a syntax
      // error are different mistakes, and one message for both sends the user
      // looking in the wrong place.
      throw expected(`${prefix}${source}: ${e instanceof Error ? e.message : 'could not be read'}`);
    }
  }
  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch (e) {
    const where = source !== undefined ? ` in ${source}` : '';
    throw expected(`${prefix}invalid JSON${where}: ${e instanceof Error ? e.message : String(e)}`);
  }
}

/** The public MCP connection URL for a toolset slug. */
export function toolsetMcpUrl(slug: string): string {
  return `https://data-mcp.wayai.pro/t/${slug}/mcp`;
}

/**
 * The Data global options, read from a FIXED level — the namespace root (`bases`,
 * `records`, …) where `withDataGlobals` declares them — rather than merged from
 * the innermost command outward. A merged read would let a subcommand's own
 * same-named option (an `export --output <file>`, say) be mistaken for the
 * output FORMAT, which is a silently wrong answer rather than an error.
 */
export function globals(cmd: Command): DataGlobals {
  let namespace: Command = cmd;
  while (namespace.parent?.parent) namespace = namespace.parent;
  return namespace.opts() as DataGlobals;
}

/**
 * Add `--base` to a command that operates against one.
 *
 * Lives here rather than in `program.ts` because the command modules need it
 * too, and importing it from there would close a cycle (`program.ts` imports
 * every command module). `program.ts` re-exports it for the namespace builders.
 *
 * Not applied universally: `bases` takes its target positionally, so advertising
 * `--base` on that namespace root documented a flag that did nothing — a user
 * who set `WAYAI_BASE` got an unscoped result with no error rather than a
 * complaint. The operational subcommands underneath it DO address one base, so
 * they each declare it (see `baseOptionHolder`).
 */
export function withBaseOption(command: Command): Command {
  return command.option('--base <id>', 'Base id (or set WAYAI_BASE)');
}

/**
 * The command that DECLARES `--base`, searching from `cmd` outward.
 *
 * `--base` cannot be read at the fixed namespace level the way `--output` is.
 * Most namespaces (`records`, `files`, …) declare it on the namespace root, but
 * `bases` deliberately does NOT — its own verbs name their target positionally,
 * and advertising a flag there that did nothing is the defect `withBaseOption`
 * was split out to fix. The operational subcommands living under `bases`
 * (`bases sql`, `bases import`, …) genuinely do address one base, so they
 * declare their own.
 *
 * Searching for the DECLARING command rather than merging option values inward
 * is what keeps this from becoming `--output`'s bug in a second place: it reads
 * the value from wherever the option actually is, and finds nothing when no
 * ancestor declares one, instead of picking up a same-named option that means
 * something else.
 */
function baseOptionHolder(cmd: Command): Command | undefined {
  for (let c: Command | undefined = cmd; c; c = c.parent ?? undefined) {
    if (c.options.some((o) => o.long === '--base')) return c;
  }
  return undefined;
}

/**
 * The explicitly-flagged `--base`, else undefined. Deliberately flag-only:
 * callers that treat `undefined` as *meaningful* use this, and folding the
 * `WAYAI_BASE` default in here would make every one of their fallbacks
 * unreachable (see `requireBase`).
 *
 * Use this or `requireBase` rather than indexing opts yourself — a call site
 * that spells the key inline drifts silently when the option is renamed.
 */
export function findBase(cmd: Command): string | undefined {
  return (baseOptionHolder(cmd)?.opts() as DataGlobals | undefined)?.base;
}

/**
 * The base for commands that *require* one. Precedence: `--base` flag >
 * `WAYAI_BASE` env > error. The env fallback lives here rather than in
 * `findBase` because this is exactly the set of commands that otherwise exit 1;
 * `pull`/`push` never require `--base`, so they keep their own contextual
 * defaults instead of being silently retargeted by a shell-wide variable.
 *
 * Deliberately does NOT apply the base-id rule. Every command reached through
 * `--base` addresses a base that already exists, over routes the API serves
 * without applying that rule — so checking it here would refuse, from the
 * client, work the server would have done. The `--base` value is still bounded:
 * it goes on to `pathSegment` at the call site, which is the check that keeps a
 * traversal segment out of the request path.
 */
export function requireBase(cmd: Command): string {
  const base = findBase(cmd) ?? process.env.WAYAI_BASE ?? '';
  if (base) return base;
  // Prints and exits rather than throwing: a missing flag is expected user
  // input, not a bug worth reporting.
  console.error('Error: --base is required (or set WAYAI_BASE)');
  return process.exit(1);
}

/**
 * The output format from `--output` / `--json`. Resolved through `globals`, so
 * it reads the namespace level at any nesting depth and never picks up a
 * subcommand's own same-named `--output`. Compared against `'json'` so an
 * unrecognized value falls back to `table` rather than a bogus format.
 */
export function outputFormat(cmd: Command): OutputFormat {
  const opts = globals(cmd);
  return opts.json || opts.output === 'json' ? 'json' : 'table';
}

/**
 * The query string shared by every audit-log read (`records history`,
 * `record-types history`, …). One builder so the four entities that expose a
 * change history cannot drift into spelling the same three params differently.
 */
export function historyQuery(opts: {
  limit?: string;
  offset?: string;
  diff?: boolean;
}): string {
  const qs = new URLSearchParams();
  if (opts.limit) qs.set('limit', opts.limit);
  if (opts.offset) qs.set('offset', opts.offset);
  if (opts.diff) qs.set('diff', 'true');
  const q = qs.toString();
  return q ? `?${q}` : '';
}

/** Split a repeatable comma-separated option into trimmed, non-empty values. */
export function splitList(value: string): string[] {
  return value
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v.length > 0);
}

/**
 * A non-negative integer flag value. `Number('10mb')` is `NaN`, and
 * `JSON.stringify(NaN)` is `null` — so an unvalidated conversion sends
 * `{"max_size": null}` and the cap the operator asked for is silently absent
 * from the created bucket, with the next oversized upload accepted.
 */
export function parseByteCount(flag: string, value: string): number {
  const n = Number(value);
  if (!Number.isInteger(n) || n < 0) {
    throw expected(`${flag} must be a whole number of bytes (0 = unlimited), not ${JSON.stringify(value)}.`);
  }
  return n;
}

/**
 * A shared secret for a Data command, read from a pipe or a masked prompt —
 * never from argv, which is readable from `/proc/<pid>/cmdline` and lands in
 * shell history and CI logs.
 *
 * The stdin-vs-prompt decision lives in `lib/utils.ts` beside the primitives it
 * guards, so this surface carries no second copy of it.
 */
export async function readSecret(
  source: { stdin: boolean | undefined; prompt: boolean | undefined; stdinFlag: string; promptHint: string },
  label: string,
): Promise<string> {
  const value =
    resolveSecretSource(source) === 'stdin'
      ? (await readStdin()).trim()
      : (await promptSecret(`${label}: `)).trim();
  if (!value) throw expected(`${label} is required.`);
  return value;
}

/**
 * True when both stdin and stdout are TTYs — the only condition under which a
 * prompt can actually be answered.
 *
 * Destructive Data commands gate their confirmation on this rather than calling
 * `confirm` unconditionally: off a TTY the readline prompt has no one to answer
 * it, so `rl.question`'s callback never fires, the promise never settles, and
 * node drains its loop and exits 0 — a CI job goes green having destroyed
 * nothing OR having been told nothing. The convention is that scripts opt in
 * with `--yes`, and a missing `--yes` off a TTY is REFUSED rather than assumed
 * in either direction.
 *
 * Distinct from `resolveSecretSource`, which answers the same TTY question for
 * secret ENTRY; this one is about confirmation prompts, and neither should be
 * expressed in terms of the other.
 */
export function isInteractive(): boolean {
  return Boolean(process.stdin.isTTY && process.stdout.isTTY);
}

const DURATION_RE = /^(\d+)([smhd])$/;
const DURATION_UNIT_MS: Record<string, number> = {
  s: 1_000,
  m: 60_000,
  h: 3_600_000,
  d: 86_400_000,
};

/**
 * Parse a human duration ("30s", "10m", "2h", "7d") into ms.
 *
 * `expected`, not a bare Error: a mistyped `--ttl` is user input, and an
 * untagged throw reaches Sentry and the autofix triage queue.
 */
export function parseDuration(input: string, flag = 'duration'): number {
  const match = DURATION_RE.exec(input.trim());
  const value = Number(match?.[1] ?? 0);
  const unit = match?.[2];
  const unitMs = unit ? DURATION_UNIT_MS[unit] : undefined;
  if (!unitMs || value <= 0) {
    throw expected(
      `Invalid ${flag} ${JSON.stringify(input)} — use <number><unit> with unit s/m/h/d (e.g. 10m, 2h, 30d).`,
    );
  }
  return value * unitMs;
}
